/*
Radio bar: a plain <audio> element and two buttons. No library, no Web Audio —
the equaliser is CSS. Live metadata (current segment, host, next up) is polled
from the radio's own endpoints.
*/
import afterLoad from './afterLoad';

const STREAM = 'https://radio.thehype.news/stream';
const RADIO_API = 'https://radio.thehype.news';
const META_POLL_MS = 30000;

export default function radio() {
    const bar = document.querySelector('.th-radio-bar');
    if (!bar) return;

    const playBtn = bar.querySelector('.th-radio-play');
    const collapseBtn = bar.querySelector('.th-radio-collapse');
    const stateEl = bar.querySelector('[data-radio-state]');
    const hostEl = bar.querySelector('[data-radio-host]');
    const nowEl = bar.querySelector('[data-radio-now]');
    const nextEl = bar.querySelector('[data-radio-next]');
    let audio = null;

    // Restore the remembered bar state (never the playing state — it's a stream)
    if (localStorage.getItem('th-player-open') === 'collapsed') {
        bar.dataset.collapsed = 'true';
        collapseBtn?.setAttribute('aria-expanded', 'false');
        collapseBtn?.setAttribute('aria-label', 'Expand player');
    }

    // Keep the page bottom clear of the fixed bar, whatever its height is.
    // The height comes from a ResizeObserver: it reports after the browser's own
    // layout, so reading offsetHeight here (a forced layout of the whole article
    // on every page load) is only the fallback for browsers without it.
    let barHeight = 0;
    const syncHeight = () => {
        const h = bar.dataset.collapsed === 'true' ? 0 : barHeight;
        document.documentElement.style.setProperty('--radio-bar-height', h + 'px');
    };
    if ('ResizeObserver' in window) {
        new ResizeObserver(([entry]) => {
            barHeight = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight);
            syncHeight();
        }).observe(bar);
    } else {
        const measure = () => {
            barHeight = bar.offsetHeight;
            syncHeight();
        };
        measure();
        window.addEventListener('resize', measure);
    }

    // Mute model — same as the radio site's player: "pause" mutes while the
    // stream keeps rolling, so unpausing is instant and always at the live
    // edge. After 2 minutes muted the stream is released to save bandwidth;
    // the next click reconnects fresh.
    const IDLE_STOP_MS = 2 * 60 * 1000;
    let idleTimer = null;

    function ensureAudio() {
        if (audio) return audio;
        audio = new Audio();
        audio.preload = 'none';
        audio.addEventListener('playing', () => setState(audio.muted ? 'paused' : 'playing'));
        audio.addEventListener('waiting', () => { if (!audio.muted) setState('loading'); });
        audio.addEventListener('pause', () => setState('paused'));
        audio.addEventListener('error', () => {
            setState('paused');
            playBtn.setAttribute('aria-label', 'Stream unavailable');
        });
        return audio;
    }

    function connect() {
        const a = ensureAudio();
        a.muted = false;
        a.src = bar.dataset.stream || STREAM;
        setState('loading');
        a.play().catch(() => setState('paused'));
    }

    playBtn?.addEventListener('click', () => {
        // First click, idle-stopped, or system-paused (media keys) — (re)connect
        // fresh so playback lands on the live edge, not stale buffer.
        if (!audio || audio.paused) {
            if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
            connect();
            return;
        }
        if (audio.muted) {
            // Back within the idle window — instant resume, still live.
            audio.muted = false;
            setState('playing');
            if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        } else {
            audio.muted = true;
            setState('paused');
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                idleTimer = null;
                if (!audio || !audio.muted || audio.paused) return;
                audio.pause();
                audio.removeAttribute('src');
                audio.load();
            }, IDLE_STOP_MS);
        }
    });

    function setState(state) {
        bar.dataset.state = state;
        bar.dataset.playing = String(state === 'playing');
        playBtn.setAttribute('aria-label',
            state === 'playing' ? 'Pause the live stream' :
            state === 'loading' ? 'Connecting to the stream' :
            'Play the live stream');
        playBtn.setAttribute('aria-pressed', String(state === 'playing'));
        if (stateEl) {
            stateEl.textContent =
                state === 'playing' ? 'On air' :
                state === 'loading' ? 'Tuning…' :
                'Paused';
        }
    }

    collapseBtn?.addEventListener('click', () => {
        const collapsed = bar.dataset.collapsed === 'true';
        bar.dataset.collapsed = String(!collapsed);
        localStorage.setItem('th-player-open', collapsed ? 'bar' : 'collapsed');
        collapseBtn.setAttribute('aria-expanded', String(collapsed));
        collapseBtn.setAttribute('aria-label', collapsed ? 'Collapse player' : 'Expand player');
        syncHeight();
    });

    // ── live metadata ────────────────────────────────────────────────
    async function refreshMeta() {
        if (document.hidden) return;
        try {
            const [now, up] = await Promise.all([
                fetch(RADIO_API + '/now-playing', { cache: 'no-store' }).then(r => (r.ok ? r.json() : null)),
                fetch(RADIO_API + '/upcoming', { cache: 'no-store' }).then(r => (r.ok ? r.json() : null)),
            ]);
            if (now && nowEl) {
                nowEl.textContent = now.type === 'music' && now.artist
                    ? now.artist + ' — ' + (now.title || '')
                    : (now.title || 'AI news radio — 24/7');
            }
            if (hostEl) {
                hostEl.textContent = now && now.host ? ' · ' + now.host : '';
            }
            if (up && nextEl) {
                const item = Array.isArray(up.items) && up.items[0];
                nextEl.textContent = item
                    ? 'Next: ' + (item.title || item.label || '') + (item.host ? ' · ' + item.host : '')
                    : '';
            }
        } catch (_) { /* keep the last known state */ }
    }
    afterLoad(startMetaPolling);

    // Every metadata request waits for the page load, the first one and the
    // repeats alike, so none of them competes with the hero image.
    function startMetaPolling() {
        refreshMeta();
        setInterval(refreshMeta, META_POLL_MS);
        document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshMeta(); });
    }
}
